import type { PeerRole, SignalPayload } from "@kitae/transfer-protocol";

export type PeerStatus = "idle" | "connecting" | "connected" | "reconnecting" | "failed";

export class LanPeer {
  readonly #role: PeerRole;
  readonly #sendSignal: (payload: SignalPayload) => Promise<void>;
  #connection?: RTCPeerConnection;
  #channel?: RTCDataChannel;
  #restartTimer?: number;
  #pendingCandidates: RTCIceCandidateInit[] = [];
  onChannel?: (channel: RTCDataChannel) => void;
  onStatus?: (status: PeerStatus) => void;
  onSessionEnded?: () => void;

  constructor(role: PeerRole, sendSignal: (payload: SignalPayload) => Promise<void>) {
    this.#role = role;
    this.#sendSignal = sendSignal;
  }

  async start(): Promise<void> {
    if (this.#role !== "receiver") return;
    this.closePeer();
    const connection = this.#createConnection();
    this.#bindChannel(connection.createDataChannel("kitae-transfer", { ordered: true }));
    const offer = await connection.createOffer();
    await connection.setLocalDescription(offer);
    await this.#sendSignal({ type: "offer", sdp: offer });
  }

  async handleSignal(payload: SignalPayload): Promise<void> {
    if (payload.type === "offer" && this.#role === "sender") {
      this.closePeer();
      const connection = this.#createConnection();
      await connection.setRemoteDescription(payload.sdp);
      await this.#flushCandidates(connection);
      const answer = await connection.createAnswer();
      await connection.setLocalDescription(answer);
      await this.#sendSignal({ type: "answer", sdp: answer });
    } else if (payload.type === "answer" && this.#role === "receiver") {
      if (this.#connection) {
        await this.#connection.setRemoteDescription(payload.sdp);
        await this.#flushCandidates(this.#connection);
      }
    } else if (payload.type === "candidate") {
      if (this.#connection?.remoteDescription) await this.#connection.addIceCandidate(payload.candidate);
      else this.#pendingCandidates.push(payload.candidate);
    } else if (payload.type === "restart" && this.#role === "receiver") {
      await this.start();
    } else if (payload.type === "bye") {
      this.closePeer();
      this.onSessionEnded?.();
    }
  }

  async endSession(): Promise<void> {
    try {
      await this.#sendSignal({ type: "bye" });
    } finally {
      this.closePeer();
    }
  }

  closePeer(): void {
    if (this.#restartTimer) clearTimeout(this.#restartTimer);
    this.#channel?.close();
    this.#connection?.close();
    this.#channel = undefined;
    this.#connection = undefined;
    this.#pendingCandidates = [];
  }

  #createConnection(): RTCPeerConnection {
    this.onStatus?.("connecting");
    const connection = new RTCPeerConnection({ iceServers: [], iceCandidatePoolSize: 0 });
    this.#connection = connection;
    connection.onicecandidate = (event) => {
      if (event.candidate) void this.#sendSignal({ type: "candidate", candidate: event.candidate.toJSON() });
    };
    connection.ondatachannel = (event) => this.#bindChannel(event.channel);
    connection.onconnectionstatechange = () => {
      const state = connection.connectionState;
      if (state === "connected") {
        if (this.#restartTimer) clearTimeout(this.#restartTimer);
        this.onStatus?.("connected");
      } else if (state === "disconnected") {
        this.onStatus?.("reconnecting");
        if (this.#role === "receiver") this.#restartTimer = window.setTimeout(() => void this.start(), 3_000);
      } else if (state === "failed") {
        this.onStatus?.("failed");
        if (this.#role === "receiver") void this.start();
      }
    };
    return connection;
  }

  #bindChannel(channel: RTCDataChannel): void {
    this.#channel = channel;
    channel.binaryType = "arraybuffer";
    channel.addEventListener("open", () => this.onChannel?.(channel), { once: true });
  }

  async #flushCandidates(connection: RTCPeerConnection): Promise<void> {
    const candidates = this.#pendingCandidates.splice(0);
    for (const candidate of candidates) await connection.addIceCandidate(candidate);
  }
}
